/**
 * @citeclerk/ui — CardSheet.jsx. RUN CC. ONE card sheet, on OUR Stripe account, reached from both
 * the Buy buttons and from Manage payment.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * 🔴 WHY IT EXISTS: PLANS AND PACKS CHARGE THROUGH TWO DIFFERENT STRIPE ACCOUNTS.
 *
 * A plan subscription is collected by Clerk Billing's <PaymentElement> (apps/main/src/checkout),
 * and the card it saves lives on CLERK'S Stripe account — one our `sk_live_` key cannot see. Packs
 * charge through OURS, off `user_billing`, which held 0 rows. So a customer who had already given
 * us a card was told by the Buy button to "add a payment method", and every account of that was a
 * lie about their own memory. This sheet is the missing half.
 *
 * ⚠️ IT IS NOT A REUSE OF THE CLERK COMPONENT, AND IT CANNOT BE. Clerk's element collects onto
 * Clerk's account by construction; that is the whole defect. It is styled to match the plan modal
 * so the two read as one product — same navy header, same 500px panel, same full-width action,
 * same Stripe badge — but the account underneath is ours.
 *
 * 🔴 ONE CLICK, AND THEREFORE THE BUTTON MUST STATE THE CHARGE. Adam's ruling is that card-entry
 * submit IS the purchase authorization: card entered → charge fires → credits deposited, no second
 * click. On a `sk_live_` key that makes the submit button the last thing standing between a lawyer
 * and a real debit, so it says the amount and the item — `Pay $10 · 100 citations`, never
 * "Save card". A person must never be able to submit a card form and be surprised by a charge.
 *
 * 🔴 RUN PX — THE IDEMPOTENCY KEY IS NOW MINTED PER SUBMIT ATTEMPT, NOT PER OPEN. It used to be
 * minted once on open and reused for every submit() call for the life of the sheet, on the theory
 * that a fresh key per click was "not idempotency at all". That reasoning missed the case that
 * actually happened: a decline followed by a DIFFERENT card, in the SAME open sheet. The second
 * submit reused the first attempt's key with a different `payment_method` — alacarte.js:542-548
 * passes this straight through as Stripe's own idempotency key — and Stripe's idempotency layer
 * rejects a reused key sent with different parameters. It does not create a new PaymentIntent and
 * it does not log a new attempt, so the retry silently never reached Stripe: same 402
 * `payment_failed` message (billing.js's generic branch — it cannot tell a real second decline
 * from an idempotency-key conflict), no new Stripe log line, card never charged. Closing and
 * reopening the sheet "fixed" it only because that path already reset `keyRef.current` to null.
 *
 * The double-submit `busy` already guards a genuine double-click (checked synchronously at the
 * top of `submit`, and the button disables while busy) — the key's only remaining job is to
 * survive ONE submit() call, not the sheet's whole lifetime. So it is minted fresh every time
 * `submit` runs, immediately before the network calls it protects.
 *
 * 🔴 IT READS `selling_enabled` BEFORE IT OFFERS A PURCHASE. RUN YA's rule, and
 * packages/ui/chargeReachability.test.mjs fails this file if it posts a charging route without it.
 * Every non-answer is SHUT — a 401, a 500, a body with no field. The calm default in this codebase
 * is the dangerous one, so the calm default here is "we are not taking your money".
 *
 * ⚠️ CARD ENTRY AND SELLING ARE TWO DIFFERENT QUESTIONS. Saving a card charges nothing, so Manage
 * payment works while the till is shut; only the PURCHASE face consults the gate. A customer whose
 * card expires must be able to fix it on a day we are not selling.
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PACKS, METER_LABEL } from '@citeclerk/plan';
import LegalLinks from './LegalLinks.jsx'; // RUN PX AMENDMENT — Terms/Privacy on every purchase surface

/** cents → "$10" / "$10.50". A checkout must never read "$10.00". Same formula as the plan modal. */
const usd = (cents) => (cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`);

/** The item, described from @citeclerk/plan — never hand-typed. "100 citations" / "1 Doc Check". */
export function packDescription(packKey) {
  const p = PACKS[packKey];
  if (!p) return null;
  const noun = p.units === 1 ? String(METER_LABEL[p.meter] || p.meter).replace(/s$/, '') : (METER_LABEL[p.meter] || p.meter);
  return { units: p.units, noun, priceCents: p.priceCents, price: usd(p.priceCents), label: p.label,
           text: `${p.units} ${noun}` };
}

/** The submit button's words. It states the amount AND the item, or it does not charge. */
export function payButtonLabel(packKey) {
  const d = packDescription(packKey);
  return d ? `Pay ${d.price} · ${d.text}` : 'Save card';
}

const CSS = `
.cc-cs-root{position:fixed;inset:0;z-index:10000}
.cc-cs-scrim{position:absolute;inset:0;background:color-mix(in srgb, var(--cc-chrome) 62%, transparent);backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px)}
.cc-cs-panel{position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);width:min(500px,94vw);max-height:92vh;overflow-y:auto;
  background:var(--cc-card,#fff);border-radius:16px;box-shadow:var(--cc-ia-shadow-modal);
  display:flex;flex-direction:column;padding:0 0 36px;font-family:'DM Sans',system-ui,sans-serif}
.cc-cs-header{background:var(--cc-chrome,#002368);padding:26px 24px 32px;border-radius:16px 16px 0 0;display:flex;flex-direction:column;
  align-items:center;text-align:center;gap:12px;position:relative;flex-shrink:0}
.cc-cs-close{position:absolute;top:14px;right:14px;width:30px;height:30px;border:.5px solid var(--cc-on-chrome-line,rgba(255,255,255,.25));
  border-radius:8px;background:var(--cc-on-chrome-fill);color:var(--cc-on-chrome-soft,rgba(255,255,255,.8));font-size:14px;cursor:pointer;
  display:flex;align-items:center;justify-content:center}
.cc-cs-close:hover{background:var(--cc-on-chrome-fill-hover);color:var(--cc-on-chrome,#fff)}
.cc-cs-eyebrow{font-size:11px;letter-spacing:.11em;text-transform:uppercase;color:var(--cc-on-chrome-faint,rgba(255,255,255,.62));font-weight:700}
.cc-cs-title{font-family:'DM Serif Display',Georgia,serif;font-size:21px;font-weight:400;color:var(--cc-on-chrome,#fff);margin:0;line-height:1.2}
.cc-cs-price{display:flex;align-items:baseline;justify-content:center;gap:5px;margin:0}
.cc-cs-amount{font-family:'DM Serif Display',Georgia,serif;font-size:52px;font-weight:400;color:var(--cc-on-chrome,#fff);line-height:1}
.cc-cs-unit{font-size:18px;color:var(--cc-on-chrome-faint,rgba(255,255,255,.62))}
.cc-cs-body{display:flex;flex-direction:column;gap:18px;padding:28px 28px 0;border-top:2px solid var(--cc-brand-a12)}
.cc-cs-field{border:1px solid var(--cc-hairline,rgba(10,22,40,.14));border-radius:12px;padding:14px 14px;background:var(--cc-bg-input);min-height:46px}
.cc-cs-field--focus{border-color:var(--cc-accent-fill,#003A8F);box-shadow:0 0 0 3px var(--cc-brand-a10)}
.cc-cs-field-row{display:flex;gap:12px}
/* RUN PX Task 3 — the native billing-address fields (country/ZIP) reuse .cc-cs-field's box but
   need a font/appearance reset the Stripe-iframe divs never did, plus real focus styling since,
   unlike those divs, these ARE the focusable, editable controls. */
select.cc-cs-field,input.cc-cs-field{font:inherit;font-size:15px;color:var(--cc-ink,#141a26);width:100%;box-sizing:border-box}
select.cc-cs-field{appearance:none;-webkit-appearance:none;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='none' stroke='%236B86B5' stroke-width='2'%3E%3Cpath d='M5 7l5 6 5-6'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 12px center;padding-right:34px}
input.cc-cs-field::placeholder{color:var(--cc-ink-muted,#6B86B5)}
select.cc-cs-field:focus-visible,input.cc-cs-field:focus-visible{outline:none;border-color:var(--cc-accent-fill,#003A8F);box-shadow:0 0 0 3px var(--cc-brand-a10)}
.cc-cs-note{font-size:12.5px;line-height:1.5;color:var(--cc-ink-muted,#6B86B5);margin:0}
.cc-cs-error{color:var(--cc-error-text,#b91c1c);font-size:14px;line-height:1.55;text-align:center;padding:4px 0}
.cc-cs-oncard{display:flex;align-items:center;justify-content:space-between;gap:12px;border:1px solid var(--cc-hairline,rgba(10,22,40,.14));
  border-radius:12px;padding:14px}
.cc-cs-brand{font-weight:600;color:var(--cc-ink,#141a26);text-transform:capitalize}
.cc-cs-link{background:none;border:none;padding:0;font:inherit;font-size:13px;color:var(--cc-accent-text,#003A8F);text-decoration:underline;
  text-underline-offset:2px;cursor:pointer}
.cc-cs-submit{width:100%;font-size:16px;font-weight:600;padding:15px 20px;border-radius:10px;border:none;cursor:pointer;
  background:var(--cc-accent-fill,#003A8F);color:var(--cc-on-accent,#fff);font-family:inherit}
.cc-cs-submit:disabled{cursor:not-allowed;background:var(--cc-brand-a15,rgba(0,58,143,.15));color:var(--cc-ink-disabled,#8a92a0)}
.cc-cs-secure{display:flex;align-items:center;justify-content:center;gap:6px;color:var(--cc-ink-muted);font-size:12px;font-weight:500;padding:2px 0}
.cc-cs-loading{display:flex;align-items:center;justify-content:center;gap:12px;color:var(--cc-ink-muted,#6B86B5);font-size:15px;padding:16px 0}
.cc-cs-spin{width:22px;height:22px;border:2px solid var(--cc-edge,rgba(10,22,40,.14));border-top-color:var(--cc-accent-fill,#003A8F);
  border-radius:50%;animation:cc-cs-spin .7s linear infinite;flex-shrink:0}
@keyframes cc-cs-spin{to{transform:rotate(360deg)}}
@media (prefers-reduced-motion:reduce){.cc-cs-spin{animation:none}}
`;
let _css = false;
function useSheetCss() {
  useEffect(() => {
    if (_css || typeof document === 'undefined') return;
    const el = document.createElement('style'); el.dataset.ccCs = '1'; el.textContent = CSS;
    document.head.appendChild(el); _css = true;
  }, []);
}

const newKey = () => {
  try { return crypto.randomUUID(); } catch { return `cc-${Date.now()}-${Math.round(Math.random() * 1e9)}`; }
};

/**
 * RUN PX Task 3 — billing-address countries. A curated subset (not exhaustive ISO-3166), covering
 * the markets @citeclerk realistically bills today, so a customer outside it is not silently
 * refused: "Other" falls through to a free-text 2-letter code. This is standard reference data
 * (country names), not internal-identifier copy, so it is outside the internal-identifier gate's
 * 3,238-string population. United States is first and default — reported to Adam as a scope call:
 * the subscription modal's Clerk/Stripe PaymentElement carries Stripe's own FULL country list, so
 * this is real parity for the countries listed and a narrower list everywhere else.
 */
export const BILLING_COUNTRIES = [
  ['US', 'United States'], ['CA', 'Canada'], ['GB', 'United Kingdom'], ['AU', 'Australia'],
  ['NZ', 'New Zealand'], ['IE', 'Ireland'], ['DE', 'Germany'], ['FR', 'France'], ['ES', 'Spain'],
  ['IT', 'Italy'], ['NL', 'Netherlands'], ['BE', 'Belgium'], ['CH', 'Switzerland'], ['AT', 'Austria'],
  ['SE', 'Sweden'], ['NO', 'Norway'], ['DK', 'Denmark'], ['FI', 'Finland'], ['PT', 'Portugal'],
  ['PL', 'Poland'], ['SG', 'Singapore'], ['HK', 'Hong Kong'], ['JP', 'Japan'], ['IN', 'India'],
  ['MX', 'Mexico'], ['BR', 'Brazil'], ['ZA', 'South Africa'], ['AE', 'United Arab Emirates'],
];

/**
 * Stripe.js is loaded FROM js.stripe.com, not bundled — Stripe requires it for PCI scope, and the
 * plan modal's Clerk element already pulls the same script on /pricing. A load failure is reported,
 * never retried into a form that cannot submit.
 */
let _stripeScript = null;
export function loadStripeJs() {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.Stripe) return Promise.resolve(window.Stripe);
  if (_stripeScript) return _stripeScript;
  _stripeScript = new Promise((resolve) => {
    const s = document.createElement('script');
    s.src = 'https://js.stripe.com/v3/';
    s.async = true;
    s.onload = () => resolve(window.Stripe || null);
    s.onerror = () => { _stripeScript = null; resolve(null); };
    document.head.appendChild(s);
  });
  return _stripeScript;
}

/**
 * One contract token, resolved to the value the browser actually computed. Used only where a value
 * must cross into something that cannot read CSS variables (Stripe's iframe). Returns '' when there
 * is no document or no such token, and Stripe then falls back to its own default — which is the
 * right direction: an unreadable card field is better than an invisible one.
 */
function token(name) {
  try { return getComputedStyle(document.documentElement).getPropertyValue(name).trim(); }
  catch { return ''; }
}

function SecureBadge() {
  return (
    <div className="cc-cs-secure" aria-label="Secure checkout">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
      Secured by Stripe
    </div>
  );
}

/**
 * 🔴 WHICH FACE THE CUSTOMER SEES, AS A PURE FUNCTION OF WHAT THE SERVER SAID.
 *
 * It is exported and driven exhaustively (cardSheet.test.mjs) rather than left as branches inside
 * a component, because "which face does a lawyer see" is the entire failure mode of a money dialog
 * and a source-level grep proves only that every face EXISTS. RUN GB1's finding one door over: a
 * dispatcher that outlives its listener renders the NEIGHBOUR, and nothing about the source says so.
 *
 * ⚠️ `gate === null` IS ITS OWN FACE, NOT A DEFAULT. While we do not know whether the till is open
 * we show no action at all — not a spinner on a button we may have to withdraw, and not a disabled
 * one either, because a disabled button still says "there is a purchase here".
 */
export function cardSheetFace({ gate, isPurchase = false, replacing = false }) {
  if (!gate) return 'asking';
  // A purchase this server will refuse is never offered. Card entry is a DIFFERENT question:
  // saving a card charges nothing, so Manage payment survives a shut till.
  if (isPurchase && gate.selling_enabled !== true) return 'selling_shut';
  const hasCard = !!gate.card;
  if (hasCard && !replacing) return 'card_on_file';
  return gate.cardEntryAvailable ? 'card_form' : 'entry_shut';
}

/** True iff this face carries a control that can move money or save a card. */
export const faceHasAction = (face) => face === 'card_form' || face === 'card_on_file';

/**
 * @param {object}   props
 * @param {boolean}  props.open
 * @param {function} props.onClose
 * @param {string?}  props.pack     a @citeclerk/plan PACKS key → PURCHASE mode. null → MANAGE mode.
 * @param {string}   props.apiBase  origin serving /api/billing/* (steel)
 * @param {function} props.onPurchased  called with the server's success body
 * @param {function} [props.onFailed]   RUN PX Task 4 — called with the customer-facing message
 *   whenever a PURCHASE attempt fails (any stage: card setup, card save, or the charge itself).
 *   Not called in Manage-payment mode — that surface takes no money, so it stays inline-only.
 *   The sheet itself still shows the SAME message inline and stays open for a retry; this is
 *   purely so a caller can also raise the shared outcome modal without a second copy of the text.
 */
export default function CardSheet({ open, onClose, pack = null, apiBase = '', onPurchased, onFailed }) {
  useSheetCss();
  const isPurchase = !!pack;
  const desc = isPurchase ? packDescription(pack) : null;

  const [gate, setGate] = useState(null);      // null = still asking. Never assume.
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [replacing, setReplacing] = useState(false);
  const [elementReady, setElementReady] = useState(false);

  // 🔴 RUN PX — minted fresh in `submit()`, per attempt. See the header for why "once, on open"
  // let a retry with a different card silently never reach Stripe.
  const keyRef = useRef(null);
  const stripeRef = useRef(null);
  const cardRef = useRef(null);     // the `cardNumber` element — the one passed as `card:` below
  const numberMountRef = useRef(null);
  const expiryMountRef = useRef(null);
  const cvcMountRef = useRef(null);
  // RUN PX Task 3 — billing address, alongside the card fields (was ZIP bundled into the single
  // Card Element; Country did not exist at all). Plain controlled inputs, not Stripe elements —
  // Stripe reads them off `billing_details.address` at confirm, same as it always read the ZIP.
  const [country, setCountry] = useState('US');
  const [postalCode, setPostalCode] = useState('');
  const [countryOther, setCountryOther] = useState('');

  useEffect(() => {
    if (!open) keyRef.current = null;
  }, [open]);

  useEffect(() => {
    if (!open) {
      setGate(null); setErr(null); setBusy(false); setReplacing(false); setElementReady(false);
      setCountry('US'); setPostalCode(''); setCountryOther('');
      return;
    }
    let alive = true;
    (async () => {
      let g = null;
      try {
        const r = await fetch(`${apiBase}/api/billing/card`, { credentials: 'include', headers: { accept: 'application/json' } });
        if (r.ok) g = await r.json();
      } catch { /* every non-answer is a shut gate — see the header */ }
      if (!alive) return;
      // ⚠️ The shape is asserted, not assumed: a body without the fields is a body we do not trust.
      setGate({
        cardEntryAvailable: g?.cardEntryAvailable === true,
        publishableKey: typeof g?.publishableKey === 'string' ? g.publishableKey : null,
        card: g?.card || null,
        selling_enabled: g?.selling_enabled === true,
      });
    })();
    return () => { alive = false; };
  }, [open, apiBase]);

  // One source of truth with cardSheetFace — a form is mounted iff the 'card_form' face is showing.
  const needsCardForm = cardSheetFace({ gate, isPurchase, replacing }) === 'card_form';

  // Mount Stripe's SPLIT card elements only when a card form is actually being shown.
  // 🔴 RUN PX Task 3 — three separate elements (number / expiry / CVC), not one bundled `card`
  // field. Adam's instruction was to make à la carte look like the subscription modal, which shows
  // expiry and CVC as separate fields; Stripe's classic bundled Card Element cannot be laid out
  // that way at all, so this switches to its split-element API (still `confirmCardSetup` below —
  // the CONFIRMATION mechanism is unchanged, only how the fields are collected).
  useEffect(() => {
    if (!open || !needsCardForm || !gate?.publishableKey
        || !numberMountRef.current || !expiryMountRef.current || !cvcMountRef.current) return;
    let alive = true;
    let numberEl = null, expiryEl = null, cvcEl = null;
    (async () => {
      const Stripe = await loadStripeJs();
      if (!alive || !Stripe) { if (alive) setErr('We couldn’t load the secure card field. Nothing was charged.'); return; }
      const stripe = Stripe(gate.publishableKey);
      stripeRef.current = stripe;
      const elements = stripe.elements();
      /* 🔴 STRIPE'S FIELDS ARE CROSS-ORIGIN IFRAMES AND CANNOT READ OUR CSS VARIABLES, so their
         colours have to be passed as concrete values, RESOLVED from the contract tokens at mount —
         same source of truth as the CSS above, no literal, and the fields move when a theme moves. */
      const style = { base: { fontFamily: "'DM Sans', system-ui, sans-serif", fontSize: '15px',
                               color: token('--cc-ink'),
                               '::placeholder': { color: token('--cc-ink-muted') } },
                       invalid: { color: token('--cc-error-text') } };
      numberEl = elements.create('cardNumber', { style });
      expiryEl = elements.create('cardExpiry', { style });
      cvcEl = elements.create('cardCvc', { style });
      numberEl.mount(numberMountRef.current);
      expiryEl.mount(expiryMountRef.current);
      cvcEl.mount(cvcMountRef.current);
      const onChange = (e) => { if (alive && e?.error) setErr(e.error.message); };
      numberEl.on('ready', () => { if (alive) setElementReady(true); });
      numberEl.on('change', onChange);
      expiryEl.on('change', onChange);
      cvcEl.on('change', onChange);
      cardRef.current = numberEl; // passed as `card:` at confirm — Stripe associates the siblings via `elements`
    })();
    return () => {
      alive = false;
      try { numberEl && numberEl.unmount(); } catch {}
      try { expiryEl && expiryEl.unmount(); } catch {}
      try { cvcEl && cvcEl.unmount(); } catch {}
      cardRef.current = null;
    };
  }, [open, needsCardForm, gate?.publishableKey]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  /**
   * ONE CLICK. Card → SetupIntent confirm → make it the default → charge → deposited.
   * ⚠️ THE ORDER IS LOAD-BEARING. The card is saved and made spendable BEFORE the charge is
   * attempted, so a failure between the two leaves a usable card and NO charge — recoverable by
   * clicking again. The reverse order would risk a charge against a card we had not finished
   * setting up, which is the failure that costs money.
   */
  const submit = useCallback(async () => {
    if (busy) return;
    setBusy(true); setErr(null);
    // 🔴 RUN PX — fresh per attempt. `busy` above already stops a genuine double-click; this key
    // only has to survive the calls this one submit() makes, so reusing it across a retry (a
    // different card after a decline) is the bug, not the protection. See the header.
    keyRef.current = newKey();
    // RUN PX Task 4 — a purchase-attempt failure also raises the shared outcome modal; the sheet
    // itself keeps showing the same sentence inline and stays open, so a retry is still one click.
    const fail = (msg) => { setErr(msg); if (isPurchase) onFailed?.(msg); };
    try {
      // 1 · Collect the card onto OUR account, if a new one is being entered.
      if (needsCardForm) {
        const si = await fetch(`${apiBase}/api/billing/card/setup-intent`, {
          method: 'POST', credentials: 'include',
          headers: { 'content-type': 'application/json' }, body: '{}' });
        const sj = await si.json().catch(() => ({}));
        if (!si.ok) { fail(sj.message || 'We couldn’t start card entry. Nothing was charged.'); return; }
        // RUN PX Task 3 — billing address travels with the card now, same as the subscription
        // modal's PaymentElement already sends. "Other" resolves to whatever 2-letter code the
        // customer typed; Stripe validates it and declines an unrecognized one on its own.
        const resolvedCountry = country === 'OTHER' ? countryOther.trim().toUpperCase() : country;
        const res = await stripeRef.current.confirmCardSetup(sj.clientSecret, {
          payment_method: {
            card: cardRef.current,
            billing_details: { address: { country: resolvedCountry || null, postal_code: postalCode || null } },
          } });
        if (res.error) {
          // Stripe's own sentence is written for a cardholder and is better than anything we would
          // paraphrase. Nothing has been charged at this point and the copy must not imply otherwise.
          fail(res.error.message || 'That card could not be saved. Nothing was charged.');
          return;
        }
        // 2 · 🔴 SAVED IS NOT SPENDABLE. This is the call that sets the customer's default.
        const save = await fetch(`${apiBase}/api/billing/card`, {
          method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ paymentMethodId: res.setupIntent.payment_method }) });
        const savej = await save.json().catch(() => ({}));
        if (!save.ok) { fail(savej.message || 'Your card was received but we couldn’t finish setting it up. Nothing was charged.'); return; }
      }

      if (!isPurchase) { onPurchased?.({ ok: true, cardOnly: true }); onClose?.(); return; }

      // 3 · The purchase. Same idempotency key for every attempt at THIS sheet's purchase.
      const r = await fetch(`${apiBase}/api/billing/buy-pack/${encodeURIComponent(pack)}`, {
        method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ idempotencyKey: keyRef.current }) });
      const j = await r.json().catch(() => ({}));
      if (r.ok && j.ok) { onPurchased?.(j); onClose?.(); return; }
      if (r.status === 500 && j.error === 'credits_not_delivered') {
        // Charged and not credited. NEVER "try again" — see routes/billing.js PURCHASE_COPY.
        fail(j.message); return;
      }
      fail(j.message || j.detail || 'We couldn’t complete that purchase.');
    } catch {
      fail('We couldn’t reach the billing service, so this purchase was not started.');
    } finally { setBusy(false); }
  }, [busy, needsCardForm, isPurchase, pack, apiBase, onPurchased, onClose, onFailed]);

  if (!open) return null;

  /* ── the faces ─────────────────────────────────────────────────────────────────────────── */
  const face = cardSheetFace({ gate, isPurchase, replacing });
  const asking = face === 'asking';
  const sellingShut = face === 'selling_shut';
  const entryShut = face === 'entry_shut';

  // RUN PX Task 3 — a card form isn't submittable until the billing address is filled in either,
  // same as the subscription modal's PaymentElement already requires before it will confirm.
  const addressComplete = !needsCardForm
    || (postalCode.trim() !== '' && (country !== 'OTHER' || countryOther.trim().length === 2));
  const canSubmit = !busy && !asking && !sellingShut && !entryShut
    && (!needsCardForm || elementReady) && addressComplete;

  return (
    <div className="cc-cs-root" role="dialog" aria-modal="true" aria-labelledby="cc-cs-title"
         data-cs-mode={isPurchase ? 'purchase' : 'manage'} data-cs-face={face}>
      <div className="cc-cs-scrim" aria-hidden="true" onClick={() => { if (!busy) onClose?.(); }} />
      <div className="cc-cs-panel">
        <div className="cc-cs-header">
          <button type="button" className="cc-cs-close" aria-label="Close" onClick={() => { if (!busy) onClose?.(); }}>✕</button>
          <div className="cc-cs-eyebrow">{isPurchase ? 'À la carte' : 'Payment method'}</div>
          <h2 className="cc-cs-title" id="cc-cs-title">
            {isPurchase ? desc.label : (gate?.card ? 'Your card on file' : 'Add a payment method')}
          </h2>
          {isPurchase && (
            <p className="cc-cs-price">
              <span className="cc-cs-amount">{desc.price}</span>
              <span className="cc-cs-unit">one-time</span>
            </p>
          )}
        </div>

        <div className="cc-cs-body">
          {asking ? (
            <div className="cc-cs-loading"><span className="cc-cs-spin" aria-hidden="true" /><span>Checking your billing details…</span></div>
          ) : sellingShut ? (
            /* Permanent until a human changes a deployment — so it names no action (RUN UB). */
            <p className="cc-cs-note">Packs aren’t on sale yet, so there’s nothing to purchase here — and nothing has been charged.</p>
          ) : entryShut ? (
            <p className="cc-cs-note">Card entry isn’t available right now. Nothing was charged.</p>
          ) : (
            <>
              {gate.card && !replacing ? (
                <div className="cc-cs-oncard">
                  <div>
                    <div className="cc-cs-brand">{gate.card.brand} •••• {gate.card.last4}</div>
                    <div className="cc-cs-note">Expires {String(gate.card.expMonth).padStart(2, '0')}/{gate.card.expYear}</div>
                  </div>
                  <button type="button" className="cc-cs-link" onClick={() => { setReplacing(true); setElementReady(false); }}>
                    Use a different card
                  </button>
                </div>
              ) : (
                <>
                  {/* RUN PX Task 3 — split fields (number / expiry / CVC), not one bundled line —
                      matches the subscription modal's layout. */}
                  <div ref={numberMountRef} className="cc-cs-field" />
                  <div className="cc-cs-field-row">
                    <div ref={expiryMountRef} className="cc-cs-field" style={{ flex: 1 }} />
                    <div ref={cvcMountRef} className="cc-cs-field" style={{ flex: 1 }} />
                  </div>
                  {/* Billing address — plain fields, same as the ZIP always was; Country did not
                      exist on this sheet at all before. */}
                  <div className="cc-cs-field-row">
                    <select className="cc-cs-field" style={{ flex: 1 }} value={country}
                            onChange={(e) => setCountry(e.target.value)} aria-label="Billing country">
                      {BILLING_COUNTRIES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
                      <option value="OTHER">Other</option>
                    </select>
                    {country === 'OTHER' ? (
                      <input className="cc-cs-field" style={{ flex: 1 }} value={countryOther}
                             onChange={(e) => setCountryOther(e.target.value)} placeholder="Country code (e.g. BR)"
                             maxLength={2} aria-label="Country code" />
                    ) : (
                      <input className="cc-cs-field" style={{ flex: 1 }} value={postalCode}
                             onChange={(e) => setPostalCode(e.target.value)} placeholder="ZIP / postal code"
                             aria-label="ZIP or postal code" />
                    )}
                  </div>
                  {country === 'OTHER' && (
                    <input className="cc-cs-field" value={postalCode}
                           onChange={(e) => setPostalCode(e.target.value)} placeholder="ZIP / postal code"
                           aria-label="ZIP or postal code" />
                  )}
                  {!elementReady && <div className="cc-cs-loading"><span className="cc-cs-spin" aria-hidden="true" /><span>Loading secure card field…</span></div>}
                </>
              )}

              {isPurchase && (
                <p className="cc-cs-note">
                  {desc.text} banked on your account.
                </p>
              )}
            </>
          )}

          {err && <div className="cc-cs-error" role="alert">{err}</div>}

          {!asking && !sellingShut && !entryShut && (
            <>
              <button type="button" className="cc-cs-submit" disabled={!canSubmit} onClick={submit}>
                {/* 🔴 THE AMOUNT AND THE ITEM, OR IT DOES NOT CHARGE. Never "Save card" in purchase mode. */}
                {busy ? (isPurchase ? 'Charging…' : 'Saving…') : (isPurchase ? payButtonLabel(pack) : 'Save card')}
              </button>
              {isPurchase && (
                <p className="cc-cs-note" style={{ textAlign: 'center' }}>
                  Your card will be charged {desc.price} now.
                </p>
              )}
              <SecureBadge />
              {/* RUN PX AMENDMENT — a purchase surface, so Terms/Privacy must be reachable from it. */}
              {isPurchase && <LegalLinks />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
